import { FC, JSX, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ContentPlayer, { ExternalPlayerBridge } from '@player/content';

import type { Content } from '@/entities/content/api/types';
import { stopPropagation } from '@/shared/lib/utils/stopPropagation';
import LogoIcon from '@/shared/ui/icons/LogoIcon';

import { buildPlayerProps } from '../lib/utils';

interface IPlayerProps {
  content: Partial<Content>;
  type: 'video' | 'slider';
  renderedVideoURL?: string;
  resetHLS?: boolean;
  isMinimized?: boolean;
  bridge?: ExternalPlayerBridge;
}

const Player: FC<IPlayerProps> = ({
  content,
  type,
  renderedVideoURL,
  resetHLS = false,
  isMinimized = false,
  bridge,
}): JSX.Element => {
  const playerConteinerElRef = useRef<HTMLDivElement>(null);
  const contentPlayerRef = useRef<ContentPlayer>();

  // ContentPlayer component setup:
  useEffect(() => {
    if (contentPlayerRef.current) return;
    if (!playerConteinerElRef?.current) return;

    contentPlayerRef.current = new ContentPlayer({
      target: playerConteinerElRef.current,
      props: buildPlayerProps(
        content,
        type,
        renderedVideoURL,
        resetHLS,
        bridge
      ),
    });
  }, [content, playerConteinerElRef, renderedVideoURL, resetHLS, bridge, type]);

  // ContentPlayer component update:
  useEffect(() => {
    if (!contentPlayerRef.current) return;

    contentPlayerRef.current.$set(
      buildPlayerProps(content, type, renderedVideoURL, resetHLS, bridge)
    );
  }, [content, renderedVideoURL, resetHLS, bridge, type]);

  // ContentPlayer component cleanup:
  useEffect(() => {
    return function destroyPlayerComponent() {
      contentPlayerRef.current?.$destroy();
    };
  }, []);

  return (
    <>
      <div
        className={twMerge(
          'pointer-events-none flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-lg bg-light-gray grayscale-0',
          !isMinimized && 'fixed opacity-0'
        )}
      >
        <LogoIcon className="grayscale" />
      </div>

      <div
        ref={playerConteinerElRef}
        className={twMerge(
          'aspect-[16/9] w-full overflow-hidden rounded-lg bg-light-gray',
          isMinimized && 'fixed bottom-1 right-1 w-[432px]'
        )}
        onClick={stopPropagation}
      />
    </>
  );
};

export default Player;
